import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BACKEND, api, fmtDate, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarClock, Download } from "lucide-react";
import { PrintButton } from "@/components/PrintButton";

const API = BACKEND;
const useLiability = (year) => useQuery({ queryKey: ["tax-liability", year], queryFn: () => api.get(`/admin/tax-liability?year=${year}`).then((r) => r.data) });

// Compact notice for the top of the admin page when a filing date is a week away or less.
export function FilingBanner() {
  const { data } = useLiability(new Date().getFullYear());
  const nf = data?.next_filing;
  if (!nf || nf.days_left > 7) return null;
  return (
    <p className="mb-4 flex items-center gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="filing-banner">
      <CalendarClock className="h-4 w-4 shrink-0" />Lodging-tax returns for {nf.period} are due {fmtDate(nf.due)} ({nf.days_left} days). FairStay owes {money(nf.fairstay_owed)}.
    </p>
  );
}

// Monthly tax per area: what FairStay owes (a liability, never revenue) vs what homeowners collected.
export function AdminTaxLiability() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = useLiability(year);
  if (!data) return null;
  const nf = data.next_filing;
  return (
    <section id="tax-liability-report" className="mb-6 rounded-3xl border bg-white p-6" data-testid="tax-liability">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-lg font-semibold">Monthly lodging-tax report {year}</h2><span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid="tax-approval-status">Tax approval: pending CPA verification</span></div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <select className="rounded-xl border px-3 py-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="tax-liability-year">
            {[0, 1, 2].map((d) => new Date().getFullYear() - d).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button asChild variant="outline" className="rounded-full" data-testid="tax-liability-download"><a href={`${API}/api/admin/tax-liability?year=${year}&format=csv`}><Download className="mr-2 h-4 w-4" />Spreadsheet</a></Button>
          <PrintButton targetId="tax-liability-report" testid="tax-liability-print" />
        </div>
      </div>
      <div className="mb-4 rounded-2xl bg-stone-50 p-4 text-sm" data-testid="tax-next-filing">
        <p className="font-semibold">Next filing: {nf.period} returns due {fmtDate(nf.due)} · {nf.days_left} days left</p>
        <p>FairStay owes <span className="font-mono-num font-semibold" data-testid="tax-next-fairstay">{money(nf.fairstay_owed)}</span> · homeowners collected <span className="font-mono-num" data-testid="tax-next-host">{money(nf.host_collected)}</span> to remit themselves</p>
        <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2" data-testid="tax-next-areas">{nf.areas.map((a) => <li key={a.area}>{a.area}: FairStay {money(a.fairstay_owed)} · homeowners {money(a.host_collected)}</li>)}</ul>
        <p className="mt-1 text-xs text-stone-500">{nf.note} {data.approval} Reminder emails go to admins and to homeowners who collected tax, on the 25th of each month.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-sm" data-testid="tax-liability-table">
          <thead className="text-xs uppercase tracking-wider text-stone-500"><tr><th className="pb-2">Month</th><th className="pb-2">Tax area</th><th className="pb-2">Stays</th><th className="pb-2">FairStay owes</th><th className="pb-2">Homeowners collected</th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.month + r.area} className="border-t" data-testid={`tax-liability-row-${r.month}-${r.area}`}>
                <td className="py-2 pr-4">{r.month}</td>
                <td className="py-2 pr-4">{r.area}{r.pending_cpa_verification && <span className="ml-1 text-xs text-amber-700">pending CPA verification</span>}</td>
                <td className="py-2 pr-4">{r.stays}</td>
                <td className="py-2 pr-4 font-mono-num">{money(r.fairstay_owed)}</td>
                <td className="py-2 font-mono-num">{money(r.host_collected)}</td>
              </tr>
            ))}
            <tr className="border-t-2 font-semibold"><td className="py-2" colSpan={3}>Total {year}</td><td className="py-2 font-mono-num" data-testid="tax-liability-total-fairstay">{money(data.totals.fairstay_owed)}</td><td className="py-2 font-mono-num" data-testid="tax-liability-total-host">{money(data.totals.host_collected)}</td></tr>
          </tbody>
        </table>
        {!data.rows.length && <p className="py-4 text-center text-stone-500" data-testid="tax-liability-empty">No paid stays with lodging tax in {year}.</p>}
      </div>
      <p className="mt-3 text-xs text-stone-500">Grouped by stay (check-in) month. Test bookings are excluded. Amounts are reduced by refunds. Tax FairStay owes is money owed to the tax office, not income.</p>
    </section>
  );
}
